import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Clock, User, Plus, Edit, Trash2, Bell, Send, Loader2,
  Wrench, DollarSign, UserCheck, MapPin, Car, Ticket,
  Pencil, Check, X
} from "lucide-react";
import { format, isSameDay, isSameMonth, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, addMonths, subMonths } from "date-fns";
import { getStatusConfig, getServiceIconStyle, getInitials, stripVehicleFromTitle } from "./jobCardUtils";
import { JobSortToggle, readJobSortPref, writeJobSortPref, compareBySchedule } from "./JobSortToggle";
import { getDisplayAddress } from "../../utils/job-form-utils";
import { formatJobScheduleDisplay } from "../../utils/dateUtils";
import { format as fmtDate } from "date-fns";
import { useTimezone } from "../../contexts/TimezoneContext";

const getDisplayTime = (job) => {
  if ((job.status === "completed" || job.status === "completed_pending_payment") && job.completed_at) {
    try { return fmtDate(new Date(job.completed_at), "h:mm a"); } catch { /* fall through */ }
  }
  return formatJobScheduleDisplay(job) || null;
};

const CalendarJobCard = ({
  job, index, onPreview, handleEdit, confirmDelete,
  handleSendReminder, handleSendToTech, sendingToTech, onRenameJob
}) => {
  const status = getStatusConfig(job.status);
  const svcStyle = getServiceIconStyle(job.service_type);
  const price = job.total_charged || job.price || 0;
  const techName = [job.assigned_to_name, ...(job.additional_technician_names || [])].filter(Boolean).join(", ") || null;
  const displayTime = getDisplayTime(job);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const startRename = (e) => {
    e.stopPropagation();
    setTitleDraft(job.title || "");
    setEditingTitle(true);
  };
  const saveRename = async (e) => {
    e?.stopPropagation();
    const t = titleDraft.trim();
    setEditingTitle(false);
    if (t && t !== job.title) await onRenameJob?.(job, t);
  };

  return (
    <div
      className="group relative min-w-0 max-w-full"
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`calendar-job-card-${job.job_id}`}
    >
      <div
        onClick={() => onPreview?.(job)}
        className="relative overflow-hidden rounded-xl cursor-pointer bg-card/60 backdrop-blur-md border border-border/60 transition-all duration-300 ease-out hover:translate-y-[-1px] hover:shadow-md hover:border-opacity-60 job-card-enter"
      >
        {/* Status accent line */}
        <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${status.gradient} opacity-80`} style={status.barStyle} />

        <div className="p-3">
          {/* Top row: Status + Price */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                {status.pulse && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${status.dot} opacity-75`} style={status.dotStyle} />}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${status.dot}`} style={status.dotStyle} />
              </span>
              <span className={`text-[10px] font-semibold tracking-wide uppercase ${status.text}`} style={status.textStyle}>{status.label}</span>
            </div>
            <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-[#FFCC00]/10 border border-[#FFCC00]/20">
              <DollarSign className="w-2.5 h-2.5 text-[#FFCC00]" />
              <span className="text-xs font-bold text-[#FFCC00] tabular-nums">
                {price.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>

          {/* Title + Service Icon */}
          <div className="flex items-start gap-2 mb-1">
            <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${svcStyle.bg}`}>
              {(job.vehicle_year || job.vehicle_make || job.service_type === "locksmith") 
                ? <Car className={`w-3 h-3 ${svcStyle.color}`} />
                : <Wrench className={`w-3 h-3 ${svcStyle.color}`} />
              }
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {editingTitle ? (
                  <div className="flex items-center gap-1 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                    <input
                      autoFocus
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(e);
                        if (e.key === "Escape") { e.stopPropagation(); setEditingTitle(false); }
                      }}
                      className="flex-1 min-w-0 h-6 px-1.5 rounded-md bg-theme-surface border border-border/60 text-[12px] text-foreground outline-none focus:border-[#FFCC00]/60"
                      placeholder="Job title"
                      data-testid={`calendar-job-title-input-${job.job_id}`}
                    />
                    <button type="button" onClick={saveRename} className="p-0.5 rounded-md text-emerald-500 hover:bg-emerald-500/10" title="Save" data-testid={`calendar-job-title-save-${job.job_id}`}>
                      <Check className="w-3 h-3" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setEditingTitle(false); }} className="p-0.5 rounded-md text-muted-foreground hover:bg-theme-hover" title="Cancel" data-testid={`calendar-job-title-cancel-${job.job_id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h4 className="font-semibold text-foreground text-[13px] leading-tight truncate font-['Manrope']">
                      {stripVehicleFromTitle(job)}
                    </h4>
                    <button
                      type="button"
                      onClick={startRename}
                      className="p-0.5 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-theme-hover flex-shrink-0 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                      title="Rename job"
                      data-testid={`calendar-rename-job-${job.job_id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    {job.job_display_id && (
                      <span className="text-[9px] font-mono text-muted-foreground/50 bg-muted/40 px-1 py-0.5 rounded flex-shrink-0">{job.job_display_id}</span>
                    )}
                  </>
                )}
              </div>
              {/* Vehicle info shown as subtitle below the title */}
              {(job.vehicle_year || job.vehicle_make || job.vehicle_model) && (
                <p className="text-[11px] text-blue-500 mt-0.5 flex items-center gap-1 truncate">
                  <Car className="w-3 h-3 shrink-0" />
                  {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")}
                </p>
              )}
              {(job.service_name || (job.service_type && job.service_type !== "other")) && !(job.vehicle_year || job.vehicle_make || job.vehicle_model) && (
                <span className="text-[11px] text-muted-foreground/70 capitalize mt-0.5 block truncate" data-testid={`calendar-job-service-${job.job_id}`}>
                  {job.service_name || job.service_type}
                </span>
              )}
            </div>
          </div>

          {/* Info: Customer + Date + Time + Address */}
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground max-w-[150px]">
              <User className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
              <span className="truncate">{job.customer_name || "Unknown"}</span>
            </div>
            {job.scheduled_date && (
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <CalendarIcon className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
                <span>{format(new Date(job.scheduled_date), "MMM d")}</span>
              </div>
            )}
            {displayTime && (
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Clock className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
                <span>{displayTime}</span>
              </div>
            )}
            {job.customer_address && (
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground max-w-[180px]">
                <MapPin className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
                <span className="truncate">{getDisplayAddress(job)}</span>
              </div>
            )}
          </div>

          {/* Store details (ticket + location) — chips truncate so a long
              location name never blows the card width on multi-location
              dashboards (matches the single-location card layout). */}
          {(job.ticket_number || job.store_location_name) && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2 min-w-0">
              {job.ticket_number && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-500 max-w-[120px] min-w-0"
                  title={job.ticket_number}
                >
                  <Ticket className="w-2.5 h-2.5 flex-shrink-0" />
                  <span className="truncate">{job.ticket_number}</span>
                </span>
              )}
              {job.store_location_name && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 max-w-[150px] min-w-0"
                  title={job.store_location_name}
                >
                  <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                  <span className="truncate">{job.store_location_name}</span>
                </span>
              )}
            </div>
          )}

          {/* Bottom: Technician + Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border/40">
            <div className="flex items-center gap-1.5 min-w-0">
              {techName ? (
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full bg-[#0046BE]/20 border border-[#0046BE]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-[#0046BE]">{getInitials(techName)}</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground truncate max-w-[80px]">{techName}</span>
                  {job.assignment_status === "accepted" && <UserCheck className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />}
                </div>
              ) : (
                <span className="text-[10px] text-muted-foreground/50 italic">Unassigned</span>
              )}
            </div>
            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              {job.assigned_to && job.assignment_status !== "accepted" && (
                <Button variant="ghost" size="icon" onClick={() => handleSendToTech(job)} disabled={sendingToTech === job.job_id}
                  className="h-6 w-6 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-md" title="Send to technician">
                  {sendingToTech === job.job_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                </Button>
              )}
              {job.status === "scheduled" && (
                <Button variant="ghost" size="icon" onClick={() => handleSendReminder(job)}
                  className="h-6 w-6 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-md" title="Send reminder">
                  <Bell className="w-3 h-3" />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => handleEdit(job)}
                className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-theme-hover rounded-md" title="Edit">
                <Edit className="w-3 h-3" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => confirmDelete(job)}
                className="h-6 w-6 text-red-400/70 hover:text-red-300 hover:bg-red-500/10 rounded-md" title="Delete">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const JobCalendarView = ({
  currentMonth, setCurrentMonth, selectedDate, setSelectedDate,
  getJobsForDate, openNewJobDialog, handleEdit, confirmDelete,
  handleSendReminder, handleSendToTech, sendingToTech, onPreview, onRenameJob,
}) => {
  const { getToday } = useTimezone();
  const weekOptions = { weekStartsOn: 1 };
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, weekOptions);
  const calendarEnd = endOfWeek(monthEnd, weekOptions);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  // Day-panel sort order — shared across Calendar/Timeline/List, remembered per device.
  const [sortOrder, setSortOrder] = useState(() => readJobSortPref() || "desc");
  const toggleSort = () => {
    const next = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(next);
    writeJobSortPref(next);
  };
  const dir = sortOrder === "asc" ? 1 : -1;
  const jobsForSelectedDate = [...getJobsForDate(selectedDate)].sort(compareBySchedule(dir));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-3 sm:gap-5">
      {/* Calendar Grid */}
      <Card className="glass-card border-theme order-2 lg:order-1">
        <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-foreground font-['Manrope'] text-base sm:text-lg">
              {format(currentMonth, "MMMM yyyy")}
            </CardTitle>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-theme-hover" data-testid="prev-month-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(getToday())}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-theme-hover" data-testid="today-btn">
                Today
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-theme-hover" data-testid="next-month-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-2 sm:px-6 pb-3 sm:pb-6">
          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
              <div key={d} className="text-center text-[10px] sm:text-xs text-muted-foreground font-medium py-1">{d}</div>
            ))}
          </div>
          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
            {calendarDays.map((day, i) => {
              const dayJobs = getJobsForDate(day);
              const isToday = isSameDay(day, getToday());
              const isSelected = isSameDay(day, selectedDate);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate(day)}
                  className={`relative aspect-square p-0.5 sm:p-1 rounded-lg transition-all text-center flex flex-col items-center justify-start
                    ${isSelected ? "bg-[#FFCC00] text-black" : "hover:bg-theme-hover"}
                    ${isToday && !isSelected ? "ring-1 ring-[#FFCC00]" : ""}
                    ${!isCurrentMonth ? "opacity-30" : ""}`}
                  data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                >
                  <span className={`text-[11px] sm:text-sm font-medium ${isSelected ? "text-black" : "text-foreground"}`}>
                    {format(day, "d")}
                  </span>
                  {dayJobs.length > 0 && (
                    <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                      {dayJobs.slice(0, 3).map((_, j) => (
                        <div key={j} className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${isSelected ? "bg-black/50" : "bg-[#FFCC00]"}`} />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Jobs Panel */}
      <Card className="glass-card border-theme order-1 lg:order-2">
        <CardHeader className="pb-2 px-3 sm:px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-foreground font-['Manrope'] text-sm sm:text-base flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-[#FFCC00]" />
              {format(selectedDate, "EEE, MMM d")}
            </CardTitle>
            <div className="flex items-center gap-1.5">
              <JobSortToggle sortOrder={sortOrder} onToggle={toggleSort} testId="calendar-sort-toggle" />
              {jobsForSelectedDate.length > 0 && (
                <span className="text-[10px] text-muted-foreground bg-theme-surface px-2 py-0.5 rounded-full">
                  {jobsForSelectedDate.length} job{jobsForSelectedDate.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
          {jobsForSelectedDate.length > 0 ? (
            <div className="grid gap-2.5">
              {jobsForSelectedDate.map((job, i) => (
                <CalendarJobCard
                  key={job.job_id}
                  job={job}
                  index={i}
                  onPreview={onPreview}
                  handleEdit={handleEdit}
                  confirmDelete={confirmDelete}
                  handleSendReminder={handleSendReminder}
                  handleSendToTech={handleSendToTech}
                  sendingToTech={sendingToTech}
                  onRenameJob={onRenameJob}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-10">
              <div className="w-12 h-12 rounded-xl bg-theme-surface border border-border/40 flex items-center justify-center mx-auto mb-3">
                <CalendarIcon className="w-5 h-5 text-muted-foreground/30" />
              </div>
              <p className="text-muted-foreground text-sm mb-1">No jobs for this date</p>
              <p className="text-[11px] text-muted-foreground/50 mb-4">Tap a date with dots to see jobs</p>
              <Button className="btn-primary text-sm" onClick={openNewJobDialog} data-testid="schedule-job-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Schedule Job
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JobCalendarView;
